import { Services } from '../services'

export type ViResponse = 'Compliant' | 'Non-Compliant' | 'N/A' | ''
export type ViCorrected = 'Yes' | 'No' | ''
export type ViStatus = 'Draft' | 'Submitted'

/** Compliant = no NEW damage since last inspection; Non-Compliant = new damage. */
export const VI_DAMAGE_QUESTION = 'No NEW physical damage to vehicle exterior since last inspection?'
/** Pre-existing smoke smell state (not used for Devan notification). */
export const VI_SMOKE_QUESTION = 'Is there an evident smoke smell in the truck?'
/** Non-Compliant = new smoke smell since last inspection (triggers notification). */
export const VI_NEW_SMOKE_QUESTION = 'Is there new smoke smell in the truck since last inspection?'
/** Legacy wording on inspections created before the 2026-07-07 meeting changes. */
export const VI_LEGACY_DAMAGE_QUESTION = 'No visible damage to vehicle exterior?'

function newGuid(): string {
    return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
              const r = (Math.random() * 16) | 0
              const v = c === 'x' ? r : (r & 0x3) | 0x8
              return v.toString(16)
          })
}

export class ViChecklistItem {
    id = ''
    sortOrder = 0
    question = ''
    response: ViResponse = ''
    comments = ''
    corrected: ViCorrected = ''
    correctedByUserId: string | null = null
    correctedOn: Date | null = null
    correctionComments = ''
    imageUrls: string[] = []
    imageFileNames: string[] = []
}

export class ViSection {
    id = ''
    sortOrder = 0
    name = ''
    notApplicable = false
    items: ViChecklistItem[] = []
    comments = ''

    static create(name: string, sortOrder: number, questions: string[]): ViSection {
        const section = new ViSection()
        section.id = newGuid()
        section.sortOrder = sortOrder
        section.name = name
        section.items = questions.map((q, i) => {
            const item = new ViChecklistItem()
            item.id = newGuid()
            item.sortOrder = i
            item.question = q
            return item
        })
        return section
    }
}

export class VehicleInspection {
    id = ''
    indexId = 0
    date: Date = new Date()
    vehicleEquipmentCode = ''
    vehicleDescription = ''
    vehicleMainCategory = ''
    vehicleAssignedToHrRef: number | null = null
    connexNumber = ''
    jobTrailerNumber = ''
    inspectorHrRef: number | null = null

    notes = ''
    signedBy = ''
    signedOn: Date | null = null

    sections: ViSection[] = []

    status: ViStatus = 'Draft'
    submittedOn: Date | null = null
    createdOn: Date = new Date()
    createdByUserId: string | null = null
    archived = false
    hourReading: number | null = null

    // Computed display fields populated by $setProperties.
    vehicleDisplay = ''
    inspectorName = ''
    vehicleAssignedToName = ''
    nonCompliantCount = 0
    nonCompliantOpenCount = 0

    static createNew(): VehicleInspection {
        const vi = new VehicleInspection()
        // Generate the root Guid client-side. Empty-string IDs fail the server-side Guid binder.
        // Sections/items follow the same pattern so item IDs are stable across save.
        vi.id = newGuid()
        vi.sections = VehicleInspection.buildSections()
        return vi
    }

    protected $setProperties(source: any, _services: Services): void {
        const code = source.vehicleEquipmentCode ?? ''
        const desc = source.vehicleDescription ?? ''
        source.vehicleDisplay = code && desc ? `${code} — ${desc}` : code || desc

        // inspectorName / vehicleAssignedToName are filled in by the list component since there's no vpUsers repo.
        source.inspectorName = source.inspectorName ?? ''
        source.vehicleAssignedToName = source.vehicleAssignedToName ?? ''

        let nonCompliant = 0
        let nonCompliantOpen = 0
        for (const section of source.sections ?? []) {
            if (section.notApplicable) continue
            for (const item of section.items ?? []) {
                if (item.response === 'Non-Compliant') {
                    nonCompliant++
                    if (item.corrected !== 'Yes') nonCompliantOpen++
                }
            }
        }
        source.nonCompliantCount = nonCompliant
        source.nonCompliantOpenCount = nonCompliantOpen
    }

    static buildSections(): ViSection[] {
        const defs: { name: string; questions: string[] }[] = [
            {
                name: 'Truck / Vehicle Information',
                questions: [
                    'Vehicle Yearly Registration is up to date?',
                    'Vehicle has current insurance card?',
                    'Vehicle has Fire Extinguisher?',
                    'Fire Extinguisher has Pin?',
                    'Fire Extinguisher has Monthly sticker?',
                    'Fire Extinguisher has Annual Sticker?',
                    'Fire Extinguisher pressure needle is in the green?',
                    'Vehicle has Lock out tag out kit? (Locks, tags)',
                    'Vehicle has a first aid kit?',
                    'First Aid kit is stocked?',
                    "Vehicle has N95's? (Field)",
                    'Vehicle has hearing protection? (Field)',
                    'Vehicle has a face shield? (Field)',
                    'Vehicle has a Spill Kit? (Field)',
                    'Vehicle has a 4 Gas Monitor? Current Calibration? (Field)',
                    'Vehicle has Eye Wash Solution? (Field)',
                    'Work Truck - Hand Tools, Corded Tools are not damaged, missing grounding prong, wires are not exposed?',
                    'Extension Cords in trucks are not damaged, missing grounding prong, wires are not exposed?',
                    'Truck Generators - Breaker works, plug is not damaged, GFCI Works?',
                    'Fall Protection Equipment is checked and maintained?',
                    VI_DAMAGE_QUESTION,
                    VI_SMOKE_QUESTION,
                    VI_NEW_SMOKE_QUESTION,
                ],
            },
            {
                name: 'Connex Info',
                questions: [
                    'Connex has a fire extinguisher?',
                    'Fire Extinguisher has annual sticker?',
                    'Fire Extinguisher has monthly sticker? (Signed)?',
                    'Fire Extinguisher has Pin?',
                    'Fire Extinguisher Pressure needle is in the green?',
                    'Connex has a First Aid Kit?',
                    'First Aid Kit is stocked?',
                    'Connex has LOTO kit?',
                    'Connex has a Spill kit?',
                    'Connex has an Eye Wash Station?',
                ],
            },
            {
                name: 'Job Trailer Info',
                questions: [
                    'Job Trailer has Fire Extinguisher?',
                    'Fire Extinguisher has current Annual Sticker?',
                    'Fire Extinguisher has monthly inspection sticker? (Signed)?',
                    'Fire Extinguisher Pressure needle is in the green?',
                    'Fire Extinguisher has pin?',
                    'Job Trailer has First Aid Kit?',
                    'First Aid kit is stocked with supplies?',
                    'Job Trailer has eye wash station?',
                    'Job Trailer has LOTO Kit?',
                    'Job Trailer has Spill Kit?',
                ],
            },
        ]
        return defs.map((d, i) => ViSection.create(d.name, i, d.questions))
    }
}
